/**
 * Custom hook for currency input fields with comma formatting
 * Provides formatted display while maintaining raw numeric values for form submission
 */

import { useState, useEffect, useRef, FocusEvent, ChangeEvent } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';
import { formatNumber, unformatNumber } from '../utils/formatters';

interface UseCurrencyInputProps {
  value: number | undefined;
  decimals?: number;
  registerProps: UseFormRegisterReturn;
}

interface UseCurrencyInputReturn {
  value: string;
  onFocus: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur: (e: FocusEvent<HTMLInputElement>) => void;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  name: string;
  ref: UseFormRegisterReturn['ref'];
}

/**
 * Hook to manage currency input with automatic comma formatting
 *
 * Usage:
 * ```tsx
 * const currencyProps = useCurrencyInput({
 *   value: watch('property.currentMarketValue'),
 *   registerProps: register('property.currentMarketValue', { valueAsNumber: true }),
 * });
 *
 * <input type="text" {...currencyProps} />
 * ```
 */
export function useCurrencyInput({
  value,
  decimals = 0,
  registerProps,
}: UseCurrencyInputProps): UseCurrencyInputReturn {
  // Use ref to track focus state to avoid re-render cycles
  const isFocusedRef = useRef(false);
  // Track the last known numeric value to detect external changes
  const lastKnownValueRef = useRef<number | undefined>(value);

  // Initialize display with formatted value
  const [displayValue, setDisplayValue] = useState(() => {
    if (value === undefined || value === null || Number.isNaN(value)) {
      return '';
    }
    return formatNumber(value, decimals);
  });

  // Sync display value when external value changes (but not when focused)
  useEffect(() => {
    // Only update if value actually changed and we're not focused
    if (!isFocusedRef.current && value !== lastKnownValueRef.current) {
      lastKnownValueRef.current = value;
      if (value === undefined || value === null || Number.isNaN(value)) {
        setDisplayValue('');
      } else {
        setDisplayValue(formatNumber(value, decimals));
      }
    }
  }, [value, decimals]);

  const handleFocus = (_e: FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = true;
    // Show raw number without commas when focused
    if (value !== undefined && value !== null && !Number.isNaN(value)) {
      setDisplayValue(value.toString());
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    isFocusedRef.current = false;

    // Parse the current input value to get the number
    const rawValue = unformatNumber(e.target.value);
    const numValue = Number.parseFloat(rawValue);

    // Update our tracking ref with the new value
    lastKnownValueRef.current = Number.isNaN(numValue) ? undefined : numValue;

    // Format with commas when blurred
    if (Number.isNaN(numValue)) {
      setDisplayValue('');
    } else {
      setDisplayValue(formatNumber(numValue, decimals));
    }

    // Call original onBlur to update form state
    if (registerProps.onBlur) {
      void registerProps.onBlur(e);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Allow only numbers, decimal point, and minus sign
    const cleanedValue = inputValue.replaceAll(/[^\d.-]/g, '');

    setDisplayValue(cleanedValue);

    // Update the actual form value (unformatted)
    const numValue = Number.parseFloat(cleanedValue);
    if (!Number.isNaN(numValue)) {
      // Update our tracking ref
      lastKnownValueRef.current = numValue;

      // Create a synthetic event with the numeric value
      // Important: Set value as string representation of number for valueAsNumber to work
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: String(numValue),
          valueAsNumber: numValue,
        },
      };
      if (registerProps.onChange) {
        void registerProps.onChange(syntheticEvent);
      }
    } else if (cleanedValue === '' || cleanedValue === '-') {
      // Update tracking ref for empty/minus
      lastKnownValueRef.current = undefined;

      // Allow empty or just minus sign
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: cleanedValue,
          valueAsNumber: Number.NaN,
        },
      };
      if (registerProps.onChange) {
        void registerProps.onChange(syntheticEvent);
      }
    }
  };

  return {
    value: displayValue,
    onFocus: handleFocus,
    onBlur: handleBlur,
    onChange: handleChange,
    name: registerProps.name,
    ref: registerProps.ref,
  };
}
